import { Box, Button, Divider, ListSubheader, Tooltip } from '@mui/material';
import { Blend, Grid2x2 } from 'lucide-react';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMenuAnchor } from '~/hooks/useMenuAnchor';
import type { CatalogMessageId } from '~/i18n';
import { BlendCandidate, BlendSource } from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { SIDE_BY_SIDE } from '../binding/hanging';
import LayoutGlyph from '../binding/LayoutGlyph';
import {
    consoleGroupSx,
    joinedButtonSx,
    READOUT_ACCENT,
    squaredEdgeSx,
} from '../chrome';
import ConsoleMenu from '../ConsoleMenu';
import { consoleChoiceItems } from './consoleChoiceItems';
import BlendBindingMark, { BlendBindingSummary } from './BlendBindingMark';
import { fusedImageOptions } from './fusedImageOptions';
import ImageNameTrigger from './ImageNameTrigger';
import { nextPairView, PairViewChoice } from './pairView';

/** A row's choice of how its pair is drawn, held by whoever owns the row. */
export interface PairView {
    readonly current: PairViewChoice;
    /** The views this pair may be drawn in, in list order. */
    readonly choices: ReadonlyArray<PairViewChoice>;
    readonly onSelect: (view: PairViewChoice) => void;
}

interface PairControlProps {
    /** Per-row blend truth: the candidates and the image fused now, if any. */
    readonly source: BlendSource;
    readonly onFuse: (candidate: BlendCandidate) => void;
    readonly onClear: () => void;
    /**
     * The row's view choice, offered when the caller owns one. A surface that
     * only picks an image, such as an empty pane, leaves it out and the
     * control shows the image alone.
     */
    readonly view?: PairView;
}

/** The message of each view's name; side by side is the row layout's. */
const VIEW_LABEL_IDS: Record<PairViewChoice, CatalogMessageId> = {
    blend: 'pairView.blend',
    sideBySide: 'rowLayout.sideBySide',
    checkerboard: 'pairView.checkerboard',
};

function ViewIcon({ view }: { readonly view: PairViewChoice }) {
    switch (view) {
        case 'sideBySide':
            return <LayoutGlyph template={SIDE_BY_SIDE} size={12} />;
        case 'checkerboard':
            return <Grid2x2 size={12} />;
        case 'blend':
            return <Blend size={12} />;
    }
}

/**
 * The icon of the view in force; pressing it steps to the next view in the
 * list, round and round. Its name says both, so the icon never carries the
 * meaning alone.
 */
function PairViewCycle({ view }: { readonly view: PairView }) {
    const intl = useIntl();
    const next = nextPairView(view.current, view.choices);
    const lowerName = (choice: PairViewChoice) =>
        intl
            .formatMessage({ id: VIEW_LABEL_IDS[choice] })
            .toLocaleLowerCase(intl.locale);
    const label = intl.formatMessage(
        { id: 'pairView.cycle' },
        { current: lowerName(view.current), next: lowerName(next) }
    );
    return (
        <Tooltip title={label} placement="top">
            <Button
                variant="picker"
                aria-label={label}
                data-testid="pair-view-cycle"
                onClick={() => view.onSelect(next)}
                sx={{
                    ...joinedButtonSx('right'),
                    px: 1,
                    color: READOUT_ACCENT,
                }}
            >
                <ViewIcon view={view.current} />
            </Button>
        </Tooltip>
    );
}

/**
 * A row's second image and the way the two are drawn, as one control.
 *
 * The view belongs here because it governs nothing else: it exists only
 * while the row has a pair, or while a row drawn apart still has to get
 * back. The icon steps through the views; the list names every view in
 * words, checkerboard among them. The icon and the image name share one
 * frame, parted by a rule, so they read as one control.
 *
 * With a pair, the name is led by how the fused image lies on the row's
 * own, as its list shows it: the registration's approval status, or the
 * same frame. With no pair yet, the control is the invitation to make one.
 * With no image to pair with, it stays, disabled, and says why.
 */
export default function PairControl({
    source,
    onFuse,
    onClear,
    view,
}: PairControlProps) {
    const intl = useIntl();
    const menu = useMenuAnchor();
    // The list opens from the whole control, so its left edge lines up with
    // the view icon when there is one, not with the name beside it.
    const groupRef = useRef<HTMLDivElement>(null);
    useChangeVersion(source);
    const candidates = source.getCandidates();
    const current = source.getCurrent();
    const hasOptions = candidates.length > 0 || current !== undefined;
    // The view choice belongs to a pair. It stays on a row that is already
    // apart with nothing fused, so an empty pane is never a dead end: the
    // way back to one pane is the same control that opened two.
    const shownView =
        current || view?.current === 'sideBySide' ? view : undefined;

    return (
        <Box ref={groupRef} data-testid="pair-control" sx={consoleGroupSx(0)}>
            {shownView ? <PairViewCycle view={shownView} /> : null}
            <ImageNameTrigger
                modality={current?.modality}
                label={current?.label}
                placeholder={intl.formatMessage({
                    id: 'fusion.blendPlaceholder',
                })}
                lead={
                    current ? (
                        <BlendBindingMark
                            binding={current.binding}
                            sizePx={12}
                        />
                    ) : undefined
                }
                tooltip={
                    current ? (
                        <BlendBindingSummary
                            binding={current.binding}
                            registrationLabel={current.registrationLabel}
                        />
                    ) : undefined
                }
                ariaLabel={
                    current
                        ? intl.formatMessage(
                              { id: 'fusion.fusedImage' },
                              {
                                  state:
                                      current.binding.kind === 'mapped'
                                          ? current.binding.approvalStatus
                                          : 'SAME_FRAME',
                              }
                          )
                        : intl.formatMessage({ id: 'fusion.addFusedImage' })
                }
                open={menu.open}
                disabledReason={
                    hasOptions
                        ? undefined
                        : intl.formatMessage({ id: 'fusion.noCandidate' })
                }
                onOpen={(trigger) => menu.onOpen(groupRef.current ?? trigger)}
                testId="pair-image-trigger"
                sx={shownView ? squaredEdgeSx('left') : undefined}
            />
            <ConsoleMenu
                anchorEl={menu.anchorEl}
                open={menu.open}
                onClose={menu.onClose}
                minWidth={264}
            >
                {shownView
                    ? [
                          <ListSubheader key="view-label">
                              {intl.formatMessage({ id: 'pairView.section' })}
                          </ListSubheader>,
                          ...consoleChoiceItems({
                              choices: shownView.choices.map((choice) => ({
                                  value: choice,
                                  label: intl.formatMessage({
                                      id: VIEW_LABEL_IDS[choice],
                                  }),
                                  icon: <ViewIcon view={choice} />,
                              })),
                              value: shownView.current,
                              onSelect: shownView.onSelect,
                              onDone: menu.onClose,
                          }),
                          <Divider key="view-divider" />,
                      ]
                    : null}
                {fusedImageOptions({
                    candidates,
                    current,
                    onFuse,
                    onClear,
                    onDone: menu.onClose,
                })}
            </ConsoleMenu>
        </Box>
    );
}
